import { useEffect, useState } from 'react'
import { Alert, Button, Checkbox, Heading, Spinner, Stack, Table, Td, Text, TextLink, Th } from '@ui'
import { getJSON, postJSON } from '../api.js'
import { useAuth } from '../auth.jsx'
import { APP_NAME } from '../brand.js'
import { walkCaption } from '../components/Photos.jsx'
import { dayRange, linkOf, mediaText, ShareNote, stateText, when } from '../components/ShareControl.jsx'
import { sameWalk } from '../components/Star.jsx'
import { ThisWeek } from '../components/WeeklyGoals.jsx'
import { formatDistance } from '../format.js'
import { useStoredState } from '../storage.js'
import { useUnit } from '../units.jsx'

const SOURCE_LABELS = {
  mmw_import: 'MapMyWalk (imported)',
  walklogger_sync: APP_NAME,
  strava_import: 'Strava (comparison)',
  osmaps_import: 'OS Maps (comparison)',
  alltrails_import: 'AllTrails (comparison)',
  komoot_import: 'Komoot (comparison)',
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', year: 'numeric' })
}

function Counts() {
  const [about, setAbout] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    getJSON('/api/about').then(setAbout).catch((err) => setError(err.message))
  }, [])

  if (error) return <Alert variant="danger">Couldn't load walk counts: {error}</Alert>
  if (!about) return null
  return (
    <>
      <Table autoWidth>
        <thead>
          <tr><th>Source</th><Th numeric>Walks</Th><th>First</th><th>Latest</th></tr>
        </thead>
        <tbody>
          {about.sources.map((s) => (
            <tr key={s.source}>
              <td><TextLink to={`/walks?source=${encodeURIComponent(s.source)}`}>{SOURCE_LABELS[s.source] ?? s.source}</TextLink></td>
              <Td numeric>{s.walks.toLocaleString('en-GB')}</Td>
              <td>{formatDate(s.first_start)}</td>
              <td>{formatDate(s.last_start)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Text as="p" muted text="small">
        Counts include duplicate recordings of the same walk; the walks table counts each walk once.
        Server {about.version}{about.git_commit && ` (${about.git_commit})`}.
      </Text>
    </>
  )
}

// Starred walks (2026-10-14), newest first, each real walk once: its counted recording when it has
// one, else the first starred recording of it.
function StarredWalks() {
  const [unit] = useUnit()
  const [walks, setWalks] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    getJSON('/api/walks').then(setWalks).catch((err) => setError(err.message))
  }, [])

  if (error) return <Alert variant="danger">Couldn't load starred walks: {error}</Alert>
  if (!walks) return null
  const starred = walks.filter((w) => w.starred)
  const kept = []
  for (const w of [...starred.filter((w) => w.is_counted), ...starred.filter((w) => !w.is_counted)]) {
    if (!kept.some((k) => sameWalk(k, w))) kept.push(w)
  }
  kept.sort((a, b) => (a.start_time < b.start_time ? 1 : -1))
  return (
    <>
      <Heading level={2} size={5} mt={4}>Starred walks</Heading>
      {kept.length === 0 ? (
        <Text as="p" muted>None yet. Star a walk on its page, or in the walks table, to list it here.</Text>
      ) : (
        <Table autoWidth hover>
          <tbody>
            {kept.map((w) => (
              <tr key={w.id}>
                <td><TextLink to={`/walks/${w.id}`}>{walkCaption({ walk_name: w.name, start_time: w.start_time })}</TextLink></td>
                <Td numeric nowrap>{formatDistance(w.distance_meters, unit)}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  )
}

// Shared links (2026-10-24), newest first, for the signed-in owner only (a public list would
// undo the unguessable links): the walk, how long the link works, what it shows, its views, and
// Copy and Stop as on the walk page's Share panel, and the link's note. Working links only, unless "Show expired and
// stopped links" (remembered per browser) is ticked; ended links still open for a signed-in
// account, so they keep Copy link.
function SharedLinks() {
  const [unit] = useUnit()
  const [showEnded, setShowEnded] = useStoredState('wl-shares-ended', false)
  const [shares, setShares] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(null)

  useEffect(() => {
    getJSON(showEnded ? '/api/shares?ended=1' : '/api/shares').then(setShares).catch((err) => setError(err.message))
  }, [showEnded])

  const copy = (share) => {
    navigator.clipboard?.writeText(linkOf(share)).then(() => setCopied(share.id)).catch(() => {})
  }
  const stop = async (share) => {
    setBusy(true)
    setError(null)
    try {
      const stopped = await postJSON(`/api/shares/${share.id}/stop`)
      setShares((list) => (showEnded
        ? list.map((s) => (s.id === stopped.id ? { ...s, ...stopped } : s))
        : list.filter((s) => s.id !== share.id)))
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (!shares && !error) return null
  return (
    <>
      <Heading level={2} size={5} mt={4}>Shared links</Heading>
      {error && <Alert variant="danger" compact>Couldn't load shared links: {error}</Alert>}
      {shares && shares.length === 0 && (
        <Text as="p" muted>{showEnded ? 'No links yet.' : 'No links working now.'} Make one with Share… on a walk's page, or Share these photos… on the Photos page.</Text>
      )}
      {shares?.length > 0 && (
        <Table responsive middle hover>
          <thead>
            <tr><th>Walk or photos</th><Th numeric>Distance</Th><th>Works</th><th>Shows</th><th>Note</th><Th numeric>Views</Th><th /></tr>
          </thead>
          <tbody>
            {shares.map((s) => {
              const ended = s.state !== 'active'
              return (
                <tr key={s.id}>
                  {s.kind === 'photos' ? (
                    <>
                      <td><TextLink to="/photos">Photos: {dayRange(s.date_from.slice(0, 10), s.date_to.slice(0, 10))}</TextLink></td>
                      <td />
                    </>
                  ) : (
                    <>
                      <td><TextLink to={`/walks/${s.walk_id}`}>{walkCaption({ walk_name: s.walk_name, start_time: s.start_time })}</TextLink></td>
                      <Td numeric nowrap muted={ended}>{formatDistance(s.distance_meters, unit)}</Td>
                    </>
                  )}
                  <Td nowrap muted={ended}>{stateText(s)}</Td>
                  <Td nowrap muted={ended}>{mediaText(s)}{s.hide_details ? (s.kind === 'photos' ? ', near home hidden' : ', ends hidden') : ''}</Td>
                  <td>
                    <ShareNote share={s} muted={ended} onSaved={(saved) => setShares((list) => list.map((x) => (x.id === saved.id ? { ...x, ...saved } : x)))} />
                  </td>
                  <Td numeric muted={ended} title={s.last_viewed_at ? `Last opened ${when(s.last_viewed_at)}` : undefined}>{s.view_count}</Td>
                  <Td textAlign="end">
                    <Stack align="center" gap={2} justify="end">
                      <Button size="sm" variant="link" p={0} onClick={() => copy(s)}>{copied === s.id ? 'Copied' : 'Copy link'}</Button>
                      {!ended && <Button size="sm" variant="danger" outline disabled={busy} onClick={() => stop(s)}>Stop</Button>}
                    </Stack>
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}
      <Checkbox
        id="shares-ended" text="small" label="Show expired and stopped links"
        checked={showEnded} onChange={(e) => setShowEnded(e.target.checked)}
      />
    </>
  )
}

// Landing page: public. Signed out, it explains the site and offers sign-in; signed in, it shows
// the walk counts and a way into the walks.
export default function Home() {
  const { status, error, signIn } = useAuth()

  return (
    <>
      <Heading size={3}>{APP_NAME}</Heading>
      <Text as="p" text="lead">A private record of walks from the {APP_NAME} app and MapMyWalk.</Text>
      {status === 'loading' && <Spinner size="sm" />}
      {status === 'signed-out' && (
        <>
          <p>This site is private. Sign in with the Google account it knows.</p>
          <Button onClick={signIn}>Sign in with Google</Button>
          {error && <Alert variant="warning" mt={3}>{error}</Alert>}
        </>
      )}
      {status === 'signed-in' && (
        <>
          <p><TextLink to="/walks">Browse the walks</TextLink>.</p>
          <ThisWeek />
          <StarredWalks />
          <SharedLinks />
          <Heading level={2} size={5} mt={4}>Recordings by app</Heading>
          <Counts />
        </>
      )}
    </>
  )
}
